import React, { useEffect, useState } from 'react';
import { Briefcase, Building2, Database, ArrowRight, AlertCircle } from 'lucide-react';
import { CRMSection } from '../../../components/integrations/CRMSection';
import { useChatbotCRMIntegration } from '../../../hooks/useChatbotCRMIntegration';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { SalesforceForm } from '../../../components/integrations/SalesforceForm';
import { HubSpotForm } from '../../../components/integrations/HubSpotForm';

export default function CRMIntegration() {
  const { chatbotPublicId } = useChatbot();
  const { colors } = useDesignColors();
  const { 
    config, 
    loading, 
    error, 
    initialized,
    initializeServices,
    updateSalesforceConfig,
    updateHubspotConfig,
    clearError
  } = useChatbotCRMIntegration();
  
  const [activeSections, setActiveSections] = useState<{
    salesforce: boolean;
    hubspot: boolean;
  }>({
    salesforce: false,
    hubspot: false
  });

  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeServices(chatbotPublicId);
    }
    
    // Clear errors when component unmounts
    return () => clearError();
  }, [chatbotPublicId, initialized, initializeServices, clearError]);

  const toggleSection = (section: 'salesforce' | 'hubspot') => {
    setActiveSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    // Effacer les erreurs lors de la bascule entre les sections
    clearError();
  };

  // Formater l'erreur pour être plus utile à l'utilisateur
  const formatErrorMessage = (errorMsg: string | null) => {
    if (!errorMsg) return null;
    
    // Messages d'erreur spécifiques par type
    if (errorMsg.includes('Missing required fields')) {
      return (
        <div className="text-sm space-y-1">
          <div className="font-medium flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Missing required information
          </div>
          <p>Please fill in all the required fields highlighted below and try again.</p>
        </div>
      );
    }
    
    if (errorMsg.includes('Failed to initialize')) {
      return (
        <div className="text-sm space-y-1">
          <div className="font-medium flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Configuration Error
          </div>
          <p>There was a problem initializing your CRM integration. Please check your credentials and try again.</p>
        </div>
      );
    }
    
    // Message par défaut
    return (
      <div className="text-sm space-y-1">
        <div className="font-medium flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          Error
        </div>
        <p>{errorMsg}</p>
      </div>
    );
  };

  if (loading && !initialized) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Introduction Section */}
      <div 
        className="p-6 rounded-xl border border-white/10"
        style={{
          background: `linear-gradient(135deg, ${colors.gradientStart}10, ${colors.gradientEnd}10)`,
          backdropFilter: 'blur(8px)'
        }}
      >
        <div className="flex items-start gap-5">
          <div 
            className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}30, ${colors.gradientEnd}30)`,
              boxShadow: `0 4px 15px ${colors.gradientStart}15`
            }}
          >
            <Database className="w-6 h-6" style={{ color: colors.gradientStart }} />
          </div>
          
          <div className="space-y-3">
            <h2 className="text-xl font-bold text-white">CRM Integrations</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              Connect your chatbot to your Customer Relationship Management systems to automatically sync leads and customer data. This allows you to:
            </p>
            
            <ul className="space-y-2 pl-1">
              {[
                'Automatically capture leads from chatbot conversations',
                'Sync contact information to your CRM in real-time',
                'Track customer interactions across platforms',
                'Generate comprehensive customer profiles'
              ].map((benefit, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: colors.gradientStart }} />
                  {benefit}
                </li>
              ))}
            </ul>
            
            <p className="text-sm text-gray-400">
              Select a CRM provider below to configure your integration. You can enable multiple providers simultaneously.
            </p>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg border border-red-500/20">
          {formatErrorMessage(error)}
        </div>
      )}

      {/* CRM Sections */}
    <div className="space-y-6">
      <CRMSection
        title="Salesforce"
        subtitle="Sync your leads with Salesforce"
          icon={<Building2 className="w-5 h-5 text-indigo-400" />}
        type="salesforce"
          isEnabled={config?.crm_type === 'salesforce' && config?.is_enabled}
          onClick={() => toggleSection('salesforce')}
          onConfigure={() => {
            toggleSection('salesforce');
            setActiveSections(prev => ({
              ...prev,
              hubspot: false
            }));
          }}
        >
          {activeSections.salesforce && (
            <SalesforceForm 
              config={{
                clientId: config?.client_id || '',
                clientSecret: config?.client_secret || '',
                instanceUrl: config?.instance_url || ''
              }}
              enabled={config?.crm_type === 'salesforce' && !!config?.is_enabled}
              userId={chatbotPublicId || ''}
            />
          )}
        </CRMSection>

      <CRMSection
        title="HubSpot"
        subtitle="Sync your leads with HubSpot"
        icon={<Briefcase className="w-5 h-5 text-indigo-400" />}
        type="hubspot"
          isEnabled={config?.crm_type === 'hubspot' && config?.is_enabled}
          onClick={() => toggleSection('hubspot')}
          onConfigure={() => {
            toggleSection('hubspot');
            setActiveSections(prev => ({
              ...prev,
              salesforce: false
            }));
          }}
        >
          {activeSections.hubspot && (
            <HubSpotForm 
              config={{
                apiKey: config?.client_id || '',
                portalId: config?.client_secret || ''
              }}
              enabled={config?.crm_type === 'hubspot' && !!config?.is_enabled}
              userId={chatbotPublicId || ''}
            />
          )}
        </CRMSection>
        </div>
    </div>
  );
}